/**
 * Simple auth check that redirects authenticated users to the customer app
 * Checks localStorage for access_token since both apps share the same domain
 */

import mixpanel from 'mixpanel-browser';
import {
  ACCESS_TOKEN,
  ACCESS_TOKEN_EXPIRE_TIME,
  LOGIN_PATH,
  REFRESH_TOKEN,
} from '../constants/authConstants';
import {getBrandFromEnv, getPlatform} from '../mixpanel/mixpanel';
import {buildAutoLoginUrl, decodeJwtClaims, EMAIL_CLAIM, hasUsableSession} from './autoLoginUrl';

const AUTHENTICATED_USER_REDIRECT_URL = process.env.GATSBY_AUTHENTICATED_USER_REDIRECT_URL;
const ENABLE_AUTH_REDIRECT_FEATURE = process.env.GATSBY_ENABLE_AUTH_REDIRECT;

// Home page and login page only, never other paths
const AUTO_LOGIN_PATHS = ['', '/', LOGIN_PATH, `${LOGIN_PATH}/`];

const brand = getBrandFromEnv();
const isMFBBrand = brand === 'MFB';

const MixpanelAutoLoginAttemptEvent = 'Website - Auto Login Attempted';

export const checkAuthAndRedirect = () => {
  try {
    // Only run in browser
    if (globalThis.window === undefined) {
      return;
    }

    let customerNumber;
    let email;

    const accessToken = localStorage.getItem(ACCESS_TOKEN);

    if (accessToken) {
      const claims = decodeJwtClaims(accessToken);
      customerNumber = claims?.CustomerNumber as string | undefined;
      email = claims?.[EMAIL_CLAIM] as string | undefined;
      if (customerNumber) {
        try {
          mixpanel.identify(customerNumber);
        } catch (error) {
          console.error('Error identifying user:', error);
        }
      }
    }

    // Feature flag
    if (!ENABLE_AUTH_REDIRECT_FEATURE) {
      return;
    }

    const isAutoLoginPage = AUTO_LOGIN_PATHS.includes(
      globalThis.window.location.pathname
    );

    const sessionUsable = hasUsableSession(
      localStorage.getItem(ACCESS_TOKEN_EXPIRE_TIME),
      localStorage.getItem(REFRESH_TOKEN)
    );

    if (isAutoLoginPage && customerNumber && isMFBBrand && sessionUsable) {
      if (AUTHENTICATED_USER_REDIRECT_URL) {
        const redirectUrl = buildAutoLoginUrl(AUTHENTICATED_USER_REDIRECT_URL, email);
        // Analytics must never block the redirect.
        try {
          // Send it now by beacon, so the redirect does not lose it in the batch queue.
          mixpanel.track(
            MixpanelAutoLoginAttemptEvent,
            {
              'Platform': getPlatform(brand),
              'Customer Number': customerNumber,
            },
            {send_immediately: true, transport: 'sendBeacon'}
          );
        } catch (error) {
          console.error('Error tracking auto login:', error);
        }
        // Replace, so Back from the account app does not come here and redirect again.
        globalThis.window.location.replace(redirectUrl);
      }
    }
  } catch (error) {
    // Fail silently
    console.error('Error checking auth:', error);
  }
};
